import { useOrganizationList, useToggleOrganizationPin } from "@/api/services/admin/organization";
import { useEnum } from "@/api/services/global/enum";
import { IconPlus } from "@/assets/icons";
import { Button } from "@/components/elements/Button";
import { Input } from "@/components/elements/Input";
import { Table } from "@/components/elements/Table";
import { EOrganizationStatus } from "@/constants/enum";
import { OrganizationFilterPopover } from "@/features/organization/components/OrganizationFilterPopover";
import { OrganizationListPagination } from "@/features/organization/components/OrganizationListPagination";
import { useOrganizationListColumns } from "@/features/organization/hooks/useOrganizationListColumns";
import { useOrganizationFilterCache } from "@/features/organization/providers/OrganizationListFilterCacheProvider";
import { useDebounce } from "@/hooks/useDebounce";
import { useFillViewportHeight } from "@/hooks/useFillViewportHeight";
import { usePagination } from "@/hooks/usePagination";
import { useTable } from "@/hooks/useTable";
import { AppRoutes } from "@/routes/config";
import { text } from "@/theme/colors";
import { getDynamicRoute } from "@/utils/url";
import { Box, Stack, Typography, styled } from "@mui/material";
import { KeyboardEvent, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

const OrganizationListPage = () => {
  const navigate = useNavigate();

  const { filterCache, setFilterCache } = useOrganizationFilterCache();

  const { data: organizationTypes } = useEnum("EOrganizationType");
  const { mutate: togglePin } = useToggleOrganizationPin();

  const pagReq = usePagination();

  const { ref: tableAreaRef, maxHeight: tableMaxHeight } = useFillViewportHeight<HTMLDivElement>({ bottomGap: 120 });

  const hasFilter = !!(
    filterCache?.externalId ||
    filterCache?.name ||
    filterCache?.productId ||
    filterCache?.lastLoginFrom ||
    filterCache?.lastLoginTo ||
    filterCache?.createdDateFrom ||
    filterCache?.createdDateTo
  );

  const { data, isLoading, isFetching } = useOrganizationList({
    page: filterCache.page,
    status: filterCache?.status || EOrganizationStatus.ACTIVE,
    search: hasFilter ? undefined : filterCache.search || undefined,
    createdDateFrom: filterCache?.createdDateFrom ?? undefined,
    createdDateTo: filterCache?.createdDateTo ?? undefined,
    externalId: filterCache?.externalId ?? undefined,
    lastLoginFrom: filterCache?.lastLoginFrom ?? undefined,
    lastLoginTo: filterCache?.lastLoginTo ?? undefined,
    name: filterCache?.name ?? undefined,
    productId: filterCache?.productId ?? undefined,
    sorts: pagReq?.sorts && pagReq?.sorts?.length > 0 ? pagReq?.sorts : undefined,
  });

  const items = data?.data ?? [];
  const pag = data?.pagination;

  const [searchInput, setSearchInput] = useState(filterCache.search);
  const appliedSearch = useRef("");

  // Sync local input when search condition changes externally (e.g. tab change).
  useEffect(() => {
    setSearchInput(filterCache.search);
  }, [filterCache.search]);

  const applySearch = (rawValue: string) => {
    const value = rawValue.trim();
    if (appliedSearch.current === value) return;
    appliedSearch.current = value;
    setFilterCache((prev) => (value === prev.search ? prev : { ...prev, search: value, page: 1 }));
  };

  // Auto-search (debounced ~1s) triggers only for empty input (clear) or 2+ chars.
  useDebounce(() => {
    const value = (searchInput ?? "").trim();
    if (value.length === 1) return;
    applySearch(value);
  }, [searchInput]);

  const handleSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") applySearch((e.target as HTMLInputElement).value);
  };

  const { columns, columnVisibility, columnOrderState } = useOrganizationListColumns({
    organizationTypes,
    togglePin,
    filterCache,
  });

  const { table } = useTable({
    columns,
    rows: items,
    onClickRow: (row) => navigate(getDynamicRoute(AppRoutes.organizationDetail, { id: row.id })),
    state: { columnVisibility, columnOrder: columnOrderState },
  });

  return (
    <Stack gap={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="row" alignItems="center" gap={2}>
          <StyledSearchField
            placeholder={hasFilter ? "フィルタ適用済み" : "組織名で検索"}
            value={hasFilter ? "" : searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            onBlur={(e) => applySearch(e.target.value)}
            disabled={hasFilter}
            clearable={!hasFilter}
          />
          <OrganizationFilterPopover />
          <Typography variant="body14Semi" color={text.primary}>
            全 {pag?.total?.toLocaleString() ?? 0} 件中 {pag?.count?.toString().padStart(2, "0") ?? "00"} 件を表示
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" gap={2}>
          <Button
            size="sm"
            sx={{
              gap: 1,
            }}
            onClick={() => navigate(AppRoutes.organizationCreate)}
          >
            <IconPlus color="currentColor" />
            新規顧客登録
          </Button>
        </Stack>
      </Stack>
      <Box ref={tableAreaRef}>
        <Table table={table} isLoading={isLoading} isFetching={isFetching} maxHeight={tableMaxHeight} />
      </Box>
      <OrganizationListPagination totalPage={pag?.totalPage} />
    </Stack>
  );
};

const StyledSearchField = styled(Input)`
  min-width: 240px;

  .MuiOutlinedInput-notchedOutline {
    border-radius: 32px !important;
  }

  .MuiInputBase-input {
    padding-left: 14px !important;
  }
  .MuiInputBase-root {
    border-radius: 32px !important;
  }
  .Mui-disabled .MuiOutlinedInput-notchedOutline {
    border-radius: 32px !important;
  }
`;

export default OrganizationListPage;
